
import { useMemo } from "react";
import ReportTitle from "./report/ReportTitle";
import ReportSections from "./report/ReportSections";
import UnifiedReportContent from "./report/UnifiedReportContent";
import ReportLayout from "./report/ui/ReportLayout";

type ReportContentProps = {
  content: string;
};

/**
 * Main component for rendering report content.
 * Uses the unified approach with specialized sub-components.
 */
const ReportContent = ({ content }: ReportContentProps) => {
  // Define major sections in preferred display order
  const majorSections = [
    "Executive Summary",
    "Market Overview",
    "Key Players and Competitive Analysis",
    "Industry Trends and Developments",
    "Future Outlook",
    "Recommendations",
  ];

  // More precise error message detection - must be a short message that's clearly an error
  // and not contain any of the report section keywords
  const isErrorMessage =
    // Must be relatively short to be an error message (not a full report)
    content.length < 500 &&
    // Must contain error-related keywords
    (content.toLowerCase().includes("error") ||
      content.toLowerCase().includes("failed") ||
      content.toLowerCase().includes("i'm sorry") ||
      content.toLowerCase().includes("couldn't generate") ||
      content.toLowerCase().includes("apologize") ||
      content.toLowerCase().includes("try again")) &&
    // Must NOT contain any of the major report sections (to avoid false positives)
    !majorSections.some((section) =>
      content.toLowerCase().includes(section.toLowerCase())
    ) &&
    // Must NOT contain typical report content indicators
    !content.toLowerCase().includes("introduction & methodology") &&
    !content.toLowerCase().includes("market size") &&
    !content.toLowerCase().includes("competitive analysis") &&
    !content.toLowerCase().includes("demographics");

  // Process the content with useMemo to avoid unnecessary re-renders
  const formattedContent = useMemo(() => {
    // Pre-process Introduction & Methodology sections to ensure they're NEVER bold
    const processedContent = content
      .replace(/\*\*(.*?Introduction.*?Methodology.*?)\*\*/gi, "$1")
      .replace(/\*(.*?Introduction.*?Methodology.*?)\*/gi, "$1")
      .replace(/<strong>(.*?Introduction.*?Methodology.*?)<\/strong>/gi, "$1")
      .replace(/<b>(.*?Introduction.*?Methodology.*?)<\/b>/gi, "$1")
      // Process citation references like [1], [2] to jump links - make them clickable
      .replace(/\[(\d+)\]/g, (match, num) => {
        return `<a href="#reference-${num}" class="text-blue-600 hover:text-blue-400 hover:underline font-semibold">[${num}]</a>`;
      });

    const sections = processedContent.split("\n\n");

    // Find which major sections are present in the content
    const presentSections = majorSections.filter((section) =>
      processedContent.includes(section)
    );

    // Find the title (first line of the content)
    const title = sections[0]?.trim();
    const remainingSections = title ? sections.slice(1) : sections;

    // Process sections - exclude references for display in chat UI
    const mainSections = remainingSections.filter(section => 
      !section.toLowerCase().includes("references") && 
      !section.toLowerCase().includes("## references")
    );

    // Store the report title in window for PDF export headers
    if (typeof window !== "undefined" && title) {
      window.reportTitle = title;
    }

    return (
      <ReportLayout>
        <ReportTitle title={title} />
        <ReportSections sections={presentSections.filter(s => s.toLowerCase() !== 'references')} />
        <UnifiedReportContent
          sections={mainSections}
          majorSections={majorSections}
        />
      </ReportLayout>
    );
  }, [content, majorSections]);

  // If it's an error message, render it directly with proper styling and no formatting
  if (isErrorMessage) {
    // Remove all formatting from error messages
    const cleanedContent = content
      .replace(/\*\*/g, "") // Remove bold formatting
      .replace(/\*/g, "") // Remove italic formatting
      .replace(/__/g, "") // Remove underline formatting
      .replace(/_/g, "") // Remove emphasis
      .replace(/##/g, "") // Remove headings
      .replace(/#/g, "") // Remove any remaining hashtags
      .replace(/<strong>([^<]+)<\/strong>/g, "$1") // Remove HTML strong tags
      .replace(/<b>([^<]+)<\/b>/g, "$1") // Remove HTML b tags
      .replace(/<em>([^<]+)<\/em>/g, "$1") // Remove HTML em tags
      .replace(/<i>([^<]+)<\/i>/g, "$1"); // Remove HTML i tags

    return (
      <div className="w-full overflow-x-hidden">
        <p className="text-red-400 text-lg">{cleanedContent}</p>
      </div>
    );
  }

  return <div className="w-full overflow-x-hidden">{formattedContent}</div>;
};

// Add declaration for window.reportTitle
declare global {
  interface Window {
    reportTitle?: string;
  }
}

export default ReportContent;
